import { createContext, useContext, useEffect, useState } from "react";
import { Rows3, Rows4 } from "lucide-react";
import { cn } from "@/lib/utils";
import { FOCUS_RING } from "./tokens";

/**
 * Densidade das listagens do painel.
 *
 * O painel serve dois usos que pedem alturas diferentes: conferir estoque ou
 * vendas (varrer muitas linhas de uma vez) e trabalhar cliente a cliente (ler
 * com calma). Uma altura só atende mal os dois.
 *
 * A escolha é da pessoa e fica salva — quem confere inventário deixa compacto e
 * não quer reescolher a cada login.
 */
const KEY = "osp_density";
const DensityContext = createContext("comfortable");

export function useDensity() {
  return useContext(DensityContext);
}

export function DensityProvider({ children }) {
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem(KEY) || "comfortable"; } catch { return "comfortable"; }
  });

  useEffect(() => {
    try { localStorage.setItem(KEY, density); } catch { /* modo privado: só não persiste */ }
  }, [density]);

  return (
    <DensityContext.Provider value={density}>
      <SetDensityContext.Provider value={setDensity}>{children}</SetDensityContext.Provider>
    </DensityContext.Provider>
  );
}

const SetDensityContext = createContext(() => {});

/** Alternador de densidade, para o cabeçalho do painel. */
export function DensityToggle({ className }) {
  const density = useDensity();
  const setDensity = useContext(SetDensityContext);
  const compact = density === "compact";
  const next = compact ? "comfortable" : "compact";
  const Icon = compact ? Rows4 : Rows3;

  return (
    <button
      type="button"
      onClick={() => setDensity(next)}
      // Botão de alternância: `aria-pressed` diz o estado, e o rótulo descreve
      // para onde vai — não o que já está valendo.
      aria-pressed={compact}
      aria-label={compact ? "Usar linhas confortáveis" : "Usar linhas compactas"}
      title={compact ? "Linhas confortáveis" : "Linhas compactas"}
      data-testid="density-toggle"
      data-ring=""
      className={cn(
        "flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 bg-white",
        "text-slate-400 hover:border-slate-300 hover:text-slate-600 transition-colors",
        FOCUS_RING, className,
      )}
    >
      <Icon size={16} aria-hidden />
    </button>
  );
}
